import * as THREE from 'three';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js';
import { RoomNode, Hallway, MuseumLayoutManager } from './topologyUtils';
import { createEntranceArch } from './entranceUtils';
import { createClassicalElements } from './RoomNode';
import {
  drawContentCanvas, textMetricsFor, planeCapacity, contentText,
} from './museumCanvas';
import { isRoamSource } from './utils';

// Constants for museum configuration
export const COLORS = {
  background: 0x000000,
  walls: 0xffffff,
  wireframe: 0x000000,
  text: 0xffffff,
  accent: 0x1da1f2, // Twitter blue
  floor: 0xffffff, // Changed from gray to white
};

// Museum model configurations
export const MUSEUM_MODELS = {
  standard: {
    name: 'Standard Museum',
    path: '/new_museum.glb',
    scale: 1.0,
    cameraPosition: { x: 0, y: 2, z: 20 },
    description: 'Simplistic polygon style with uniform display panels',
    textureStrategy: 'fixed' // Use fixed canvas size
  },
  gallery: {
    name: 'Picture Gallery',
    path: '/the_picture_gallery.glb',
    scale: 100.0,
    cameraPosition: { x: -6.26, y: 7.71, z: -6.13 }, // Positioned for 100x scaled model
    description: 'Realistic scanned gallery with diverse canvas sizes',
    textureStrategy: 'fixed' // Temporarily use fixed to test scaling
  },
  palace: {
    name: 'The Endless Palace',
    type: 'generated', // built from the archive itself, no GLB
    scale: 1.0,
    cameraPosition: { x: 0, y: 1.7, z: -2 },
    description: 'A generated classical palace — every save hung, one wing per topic, rooms built as you walk'
  }
};

// Create a dome with walls and entrances
export const createDome = (roomNode) => {
  const { radius, height, position, entrances = [], themeColor } = roomNode;
  const { x, y, z } = position;
  const segments = 32; // Number of segments for the dome
  const group = new THREE.Group();
  group.position.set(x, y, z);
  
  // Create the dome/roof part
  const domeGeometry = new THREE.SphereGeometry(
    radius,
    segments,
    segments / 2,
    0,
    Math.PI * 2,
    0,
    Math.PI / 2
  );
  
  const domeMaterial = new THREE.MeshStandardMaterial({
    color: themeColor || COLORS.walls,
    side: THREE.DoubleSide,
    roughness: 0.5,
    metalness: 0.2,
  });
  
  const dome = new THREE.Mesh(domeGeometry, domeMaterial);
  dome.position.y = height / 2;
  dome.castShadow = true;
  dome.receiveShadow = true;
  group.add(dome);
  
  // Add wireframe to the dome
  const domeWireframe = new THREE.LineSegments(
    new THREE.EdgesGeometry(domeGeometry),
    new THREE.LineBasicMaterial({ color: COLORS.wireframe, linewidth: 2 })
  );
  domeWireframe.position.y = height / 2;
  group.add(domeWireframe);
  
  // Create a single complete cylindrical wall
  const wallGeometry = new THREE.CylinderGeometry(
    radius,
    radius,
    height * 0.5, // Lower height for walls
    segments,  // Full segments for smooth cylinder
    1,
    true
  );
  
  const wallMaterial = new THREE.MeshStandardMaterial({
    color: themeColor || COLORS.walls,
    side: THREE.DoubleSide,
    roughness: 0.5,
    metalness: 0.2,
  });
  
  const wall = new THREE.Mesh(wallGeometry, wallMaterial);
  wall.position.y = height * 0.25; // Position to align with the bottom of the dome
  wall.castShadow = true;
  wall.receiveShadow = true;
  // group.add(wall);
  
  // Add wireframe to the wall
  const wallWireframe = new THREE.LineSegments(
    new THREE.EdgesGeometry(wallGeometry),
    new THREE.LineBasicMaterial({ color: COLORS.wireframe, linewidth: 2 })
  );
  wallWireframe.position.y = height * 0.25;
  // group.add(wallWireframe);
  
  // For each entrance, create a entrance arch
  entrances.forEach(entrance => {
    createEntranceArch(group, entrance, radius, height);
  });
  
  return group;
};

// Create a hallway connecting two entrances - using BoxGeometry for simplicity and reliability
export const createHallway = (hallwayData, parent) => {
  const { startEntrance, endEntrance } = hallwayData;
  
  // Generate path for hallway
  const pathPoints = hallwayData.generatePath(2); 
  
  // Get start and end points
  const startPoint = pathPoints[0];
  const endPoint = pathPoints[pathPoints.length - 1];
  
  // Calculate hallway dimensions
  const hallwayWidth = Math.min(startEntrance.width, endEntrance.width) * 0.9;
  const hallwayHeight = Math.min(startEntrance.height, endEntrance.height) * 0.9;
  
  // Calculate direction and distance
  const direction = new THREE.Vector3().subVectors(
    new THREE.Vector3(endPoint.x, endPoint.y, endPoint.z),
    new THREE.Vector3(startPoint.x, startPoint.y, startPoint.z)
  );
  
  const distance = direction.length();
  direction.normalize();
  
  // Create a group for the hallway
  const hallwayGroup = new THREE.Group();
  
  // SIMPLIFY: Use BoxGeometry for the hallway walls without end caps
  
  // Side walls material
  const wallMaterial = new THREE.MeshStandardMaterial({
    color: COLORS.walls,
    roughness: 0.5,
    metalness: 0.2
  });
  
  // Floor material
  const floorMaterial = new THREE.MeshStandardMaterial({
    color: COLORS.floor,
    roughness: 0.8,
    metalness: 0.2
  });
  
  // Create the hallway structure as a simple box WITHOUT end caps
  // Using segments to allow ends to be open
  const segments = 4; // Number of segments along hallway length
  
  // Create left and right walls (stretched boxes)
  const wallThickness = 0.2;
  const wallGeometry = new THREE.BoxGeometry(distance, hallwayHeight, wallThickness);
  
  // Left wall
  const leftWall = new THREE.Mesh(wallGeometry, wallMaterial);
  leftWall.position.set(0, hallwayHeight/2, hallwayWidth/2);
  leftWall.castShadow = true;
  leftWall.receiveShadow = true;
  hallwayGroup.add(leftWall);
  
  // Right wall
  const rightWall = new THREE.Mesh(wallGeometry, wallMaterial);
  rightWall.position.set(0, hallwayHeight/2, -hallwayWidth/2);
  rightWall.castShadow = true;
  rightWall.receiveShadow = true;
  hallwayGroup.add(rightWall);
  
  // Ceiling
  const ceilingGeometry = new THREE.BoxGeometry(distance, wallThickness, hallwayWidth);
  const ceiling = new THREE.Mesh(ceilingGeometry, wallMaterial);
  ceiling.position.set(0, hallwayHeight, 0);
  ceiling.castShadow = true;
  ceiling.receiveShadow = true;
  hallwayGroup.add(ceiling);
  
  // Floor
  const floorGeometry = new THREE.BoxGeometry(distance, wallThickness, hallwayWidth);
  const floor = new THREE.Mesh(floorGeometry, floorMaterial);
  floor.position.set(0, 0, 0);
  floor.receiveShadow = true; // Floor typically only receives shadows
  hallwayGroup.add(floor);
  
  // Add wireframes to all elements
  [leftWall, rightWall, ceiling, floor].forEach(element => {
    const wireframe = new THREE.LineSegments(
      new THREE.EdgesGeometry(element.geometry),
      new THREE.LineBasicMaterial({ color: COLORS.wireframe, linewidth: 1 })
    );
    element.add(wireframe);
  });
  
  // Position the hallway at midpoint
  const midPoint = {
    x: (startPoint.x + endPoint.x) / 2,
    y: 0,
    z: (startPoint.z + endPoint.z) / 2
  };
  
  hallwayGroup.position.set(midPoint.x, midPoint.y, midPoint.z);
  
  // Rotate hallway to align with direction
  const angle = Math.atan2(direction.z, direction.x);
  hallwayGroup.rotation.y = angle;
  
  console.log(`Hallway from ${hallwayData.startRoomId} to ${hallwayData.endRoomId}:`);
  console.log(`  Direction angle: ${(angle * 180 / Math.PI).toFixed(2)} degrees`);
  console.log(`  Width: ${hallwayWidth.toFixed(2)}, Height: ${hallwayHeight.toFixed(2)}`);
  
  // Store hallway data and add to parent
  hallwayGroup.userData.hallway = hallwayData;
  parent.add(hallwayGroup);
  
  return hallwayGroup;
};

// Add tweet frames to a dome
export const addTweetFramesToDome = (dome, tweets, roomNode) => {
  const radius = roomNode.radius;
  if (!tweets || tweets.length === 0) return;
  
  const count = Math.min(tweets.length, 12);
  
  // Determine positions of entrances to avoid placing frames there
  const entranceAngles = roomNode.entrances.map(entrance => entrance.angle);
  
  // Calculate available angles for frames, avoiding entrances
  const availableAngles = [];
  const totalAngle = Math.PI * 2;
  const angleStep = totalAngle / count;
  
  for (let i = 0; i < count; i++) {
    let frameAngle = i * angleStep;
    
    // Check if this angle is too close to an entrance
    const tooCloseToEntrance = entranceAngles.some(entranceAngle => {
      const angleDiff = Math.abs((frameAngle - entranceAngle + Math.PI) % (Math.PI * 2) - Math.PI);
      return angleDiff < 0.3; // Minimum angle difference (in radians)
    });
    
    // If too close, adjust slightly
    if (tooCloseToEntrance) {
      frameAngle += 0.3; // Adjust by 0.3 radians (about 17 degrees)
    }
    
    availableAngles.push(frameAngle);
  }
  
  // Create frames at the available angles
  for (let i = 0; i < count; i++) {
    const tweet = tweets[i];
    const angle = availableAngles[i];
      
    // Position on the cylinder wall
    const x = radius * Math.cos(angle);
    const z = radius * Math.sin(angle);
    
    // Frame dimensions - larger frames for better visibility
    const frameWidth = 4.5; // Increased from 3
    const frameHeight = 3 + Math.sin(angle * 2) * 0.3; // Increased from 2
    
    // Create outer frame (Twitter blue frame)
    const outerFrameGeometry = new THREE.PlaneGeometry(frameWidth + 0.3, frameHeight + 0.3);
    const outerFrameMaterial = new THREE.MeshStandardMaterial({
      color: COLORS.accent, // Twitter blue color
      roughness: 0.5,
      metalness: 0.3,
      side: THREE.DoubleSide,
    });
    
    const outerFrame = new THREE.Mesh(outerFrameGeometry, outerFrameMaterial);
    
    // Create inner frame (canvas background)
    const frameGeometry = new THREE.PlaneGeometry(frameWidth, frameHeight);
    const frameMaterial = new THREE.MeshBasicMaterial({
      color: 0xffffff, // Pure white
      side: THREE.DoubleSide,
    });
    
    const frame = new THREE.Mesh(frameGeometry, frameMaterial);
    frame.position.z = 0.01; // Slightly in front of outer frame
    outerFrame.add(frame);
    
    const normal = new THREE.Vector3(x, 0, z).normalize();
    const offset = -1.4; // Increased offset to prevent wall clipping
    
    // Position frame on the wall with slight offset to prevent clipping
    // Vary y position slightly based on angle for visual interest
    const yPos = 4 + Math.sin(angle * 3) * 0.5; // Vary height between 3.5 and 4.5
    
    outerFrame.position.set(
      x - normal.x * offset,
      yPos,
      z - normal.z * offset
    );
    
    // Calculate tangent vector along wall (perpendicular to normal vector)
    const tangent = new THREE.Vector3(-normal.z, 0, normal.x).normalize();
    
    // Create rotation matrix using normal and tangent
    const m = new THREE.Matrix4().makeBasis(
      tangent,
      new THREE.Vector3(0, 1, 0),
      normal.clone().multiplyScalar(-1) // Flip normal to face inward
    );
    
    // Apply correct orientation from rotation matrix
    outerFrame.setRotationFromMatrix(m);
    
    // Add frame borders - inner border (around content)
    const borderGeometry = new THREE.EdgesGeometry(frameGeometry);
    const borderMaterial = new THREE.LineBasicMaterial({
      color: COLORS.accent,
      linewidth: 3, // Thicker border
    });
    
    const border = new THREE.LineSegments(borderGeometry, borderMaterial);
    frame.add(border);
    
    // Add outer border (around frame)
    const outerBorderGeometry = new THREE.EdgesGeometry(outerFrameGeometry);
    const outerBorderMaterial = new THREE.LineBasicMaterial({
      color: 0x0a5080, // Darker blue for contrast with the Twitter blue
      linewidth: 3, // Thicker border
    });
    
    const outerBorder = new THREE.LineSegments(outerBorderGeometry, outerBorderMaterial);
    outerFrame.add(outerBorder);
    
    // Add tweet text
    const textMaterial = new THREE.MeshBasicMaterial({
      color: 0x000000,
      side: THREE.DoubleSide,
    });
    
    // Create canvas based on content type
    const texture = createTweetCanvasTexture(tweet);
    
    // Apply texture to frame
    frame.material = new THREE.MeshBasicMaterial({
      map: texture,
      side: THREE.DoubleSide,
    });
    
    // Add frame to dome
    dome.add(outerFrame);
  }
};

// Add placeholder frames to a dome when no tweets are available
export const addPlaceholderFramesToDome = (dome, count, roomNode) => {
  const radius = roomNode.radius;
  // Determine position of entrances to avoid placing frames there
  const entranceAngles = roomNode.entrances.map(entrance => entrance.angle);
  
  // Calculate available angles for frames, avoiding entrances
  const availableAngles = [];
  const totalAngle = Math.PI * 2;
  const angleStep = totalAngle / count;
  
  for (let i = 0; i < count; i++) {
    let frameAngle = i * angleStep;
    
    // Check if this angle is too close to an entrance
    const tooCloseToEntrance = entranceAngles.some(entranceAngle => {
      const angleDiff = Math.abs((frameAngle - entranceAngle + Math.PI) % (Math.PI * 2) - Math.PI);
      return angleDiff < 0.3; // Minimum angle difference (in radians)
    });
    
    // If too close, try to adjust slightly
    if (tooCloseToEntrance) {
      frameAngle += 0.3; // Adjust by 0.3 radians (about 17 degrees)
    }
    
    availableAngles.push(frameAngle);
  }
  
  // Create frames at the available angles
  for (let i = 0; i < count; i++) {
    const angle = availableAngles[i];
      
    // Position on the cylinder wall
    const x = radius * Math.cos(angle);
    const z = radius * Math.sin(angle);
    
    // Frame dimensions - larger frames for better visibility
    const frameWidth = 4.5; // Increased from 3
    const frameHeight = 3 + Math.sin(angle * 2) * 0.3; // Increased from 2
    
    // Create outer frame (Twitter blue frame)
    const outerFrameGeometry = new THREE.PlaneGeometry(frameWidth + 0.3, frameHeight + 0.3);
    const outerFrameMaterial = new THREE.MeshStandardMaterial({
      color: COLORS.accent, // Twitter blue color
      roughness: 0.5,
      metalness: 0.3,
      side: THREE.DoubleSide,
    });
    
    const outerFrame = new THREE.Mesh(outerFrameGeometry, outerFrameMaterial);
    
    // Create inner frame (canvas background)
    const frameGeometry = new THREE.PlaneGeometry(frameWidth, frameHeight);
    const frameMaterial = new THREE.MeshBasicMaterial({
      color: 0xffffff, // Pure white
      side: THREE.DoubleSide,
    });
    
    const frame = new THREE.Mesh(frameGeometry, frameMaterial);
    frame.position.z = 0.01; // Slightly in front of outer frame
    outerFrame.add(frame);
    
    const normal = new THREE.Vector3(x, 0, z).normalize();
    const offset = 0.4; // Increased offset to prevent wall clipping
    
    // Position frame on the wall with slight offset to prevent clipping
    // Vary y position slightly based on angle for visual interest
    const yPos = 4 + Math.sin(angle * 3) * 0.5; // Vary height between 3.5 and 4.5
    
    outerFrame.position.set(
      x - normal.x * offset,
      yPos,
      z - normal.z * offset
    );
    
    // Calculate tangent vector along wall (perpendicular to normal vector)
    const tangent = new THREE.Vector3(-normal.z, 0, normal.x).normalize();
    
    // Create rotation matrix using normal and tangent
    const m = new THREE.Matrix4().makeBasis(
      tangent,
      new THREE.Vector3(0, 1, 0),
      normal.clone().multiplyScalar(-1) // Flip normal to face inward
    );
    
    // Apply correct orientation from rotation matrix
    outerFrame.setRotationFromMatrix(m);
    
    // Add frame borders - inner border (around content)
    const borderGeometry = new THREE.EdgesGeometry(frameGeometry);
    const borderMaterial = new THREE.LineBasicMaterial({
      color: COLORS.accent,
      linewidth: 3, // Thicker border
    });
    
    const border = new THREE.LineSegments(borderGeometry, borderMaterial);
    frame.add(border);
    
    // Add outer border (around frame)
    const outerBorderGeometry = new THREE.EdgesGeometry(outerFrameGeometry);
    const outerBorderMaterial = new THREE.LineBasicMaterial({
      color: 0x0a5080, // Darker blue for contrast with the Twitter blue
      linewidth: 3, // Thicker border
    });
    
    const outerBorder = new THREE.LineSegments(outerBorderGeometry, outerBorderMaterial);
    outerFrame.add(outerBorder);
    
    // Create canvas for placeholder text
    const canvas = document.createElement('canvas');
    const context = canvas.getContext('2d');
    canvas.width = 1024; // Higher resolution for better text rendering
    canvas.height = 512;
    
    // Fill with white background
    context.fillStyle = '#ffffff';
    context.fillRect(0, 0, canvas.width, canvas.height);
    
    // Add subtle border inside the canvas
    context.strokeStyle = '#1da1f2'; // Twitter blue
    context.lineWidth = 5;
    context.strokeRect(20, 20, canvas.width - 40, canvas.height - 40);
    
    // Draw placeholder text
    context.fillStyle = '#000000';
    context.font = 'bold 72px Arial'; // Even larger font
    context.textAlign = 'center';
    context.fillText('Tweet Display', canvas.width / 2, 180);
    
    // Add decorative underline
    context.strokeStyle = '#1da1f2';
    context.lineWidth = 4;
    context.beginPath();
    context.moveTo(canvas.width/2 - 250, 200);
    context.lineTo(canvas.width/2 + 250, 200);
    context.stroke();
    
    context.font = '56px Arial'; // Larger font
    context.fillText('Frame ' + (i + 1), canvas.width / 2, 300);
    
    // Create texture from canvas
    const texture = new THREE.CanvasTexture(canvas);
    texture.needsUpdate = true;
    
    // Apply texture to frame
    frame.material = new THREE.MeshBasicMaterial({
      map: texture,
      side: THREE.DoubleSide,
    });
    
    // Add frame to dome
    dome.add(outerFrame);
  }
};

// Add decorative floating elements to the scene
const addDecorativeElements = (scene) => {
  // Create a group for decorative elements
  const decorations = new THREE.Group();
  
  // Create several random geometric elements
  const shapes = [
    // Create a floating sphere
    () => {
      const geometry = new THREE.SphereGeometry(1, 16, 16);
      const material = new THREE.MeshStandardMaterial({
        color: 0xffffff, // Always pure white for decorative elements
        transparent: true,
        opacity: 0.7,
        wireframe: false,
      });
      const mesh = new THREE.Mesh(geometry, material);
      
      // Add wireframe
      const wireframe = new THREE.LineSegments(
        new THREE.EdgesGeometry(geometry),
        new THREE.LineBasicMaterial({ color: COLORS.wireframe })
      );
      mesh.add(wireframe);
      
      return mesh;
    },
    
    // Create a floating cube
    () => {
      const geometry = new THREE.BoxGeometry(1.5, 1.5, 1.5);
      const material = new THREE.MeshStandardMaterial({
        color: 0xffffff, // Always pure white for decorative elements
        transparent: true,
        opacity: 0.7,
        wireframe: false,
      });
      const mesh = new THREE.Mesh(geometry, material);
      
      // Add wireframe
      const wireframe = new THREE.LineSegments(
        new THREE.EdgesGeometry(geometry),
        new THREE.LineBasicMaterial({ color: COLORS.wireframe })
      );
      mesh.add(wireframe);
      
      return mesh;
    },
    
    // Create a floating torus
    () => {
      const geometry = new THREE.TorusGeometry(1, 0.4, 16, 32);
      const material = new THREE.MeshStandardMaterial({
        color: 0xffffff, // Always pure white for decorative elements
        transparent: true,
        opacity: 0.7,
        wireframe: false,
      });
      const mesh = new THREE.Mesh(geometry, material);
      
      // Add wireframe
      const wireframe = new THREE.LineSegments(
        new THREE.EdgesGeometry(geometry),
        new THREE.LineBasicMaterial({ color: COLORS.wireframe })
      );
      mesh.add(wireframe);
      
      return mesh;
    },
    
    // Create a floating octahedron
    () => {
      const geometry = new THREE.OctahedronGeometry(1);
      const material = new THREE.MeshStandardMaterial({
        color: 0xffffff, // Always pure white for decorative elements
        transparent: true,
        opacity: 0.7,
        wireframe: false,
      });
      const mesh = new THREE.Mesh(geometry, material);
      
      // Add wireframe
      const wireframe = new THREE.LineSegments(
        new THREE.EdgesGeometry(geometry),
        new THREE.LineBasicMaterial({ color: COLORS.wireframe })
      );
      mesh.add(wireframe);
      
      return mesh;
    },
  ];
  
  // Generate about 12 random elements
  for (let i = 0; i < 12; i++) {
    // Choose a random shape function
    const shapeIndex = Math.floor(Math.random() * shapes.length);
    const element = shapes[shapeIndex]();
    
    // Position randomly within a reasonable area
    const radius = 15 + Math.random() * 20;
    const angle = Math.random() * Math.PI * 2;
    const height = 5 + Math.random() * 15;
    
    element.position.set(
      radius * Math.cos(angle),
      height,
      radius * Math.sin(angle)
    );
    
    // Store original Y position for animation
    element.userData.originalY = height;
    
    // Random rotation
    element.rotation.set(
      Math.random() * Math.PI * 2,
      Math.random() * Math.PI * 2,
      Math.random() * Math.PI * 2
    );
    
    // Random scale (but keep aspect ratio)
    const scale = 0.5 + Math.random() * 1.5;
    element.scale.set(scale, scale, scale);
    
    // Add to decorations group
    decorations.add(element);
    
    // Add animation data as a property to use in animation loop
    element.userData.animation = {
      rotationSpeed: {
        x: (Math.random() - 0.5) * 0.005,
        y: (Math.random() - 0.5) * 0.005,
        z: (Math.random() - 0.5) * 0.005,
      },
      floatSpeed: 0.2 + Math.random() * 0.8,
      floatHeight: 0.5 + Math.random() * 1.0,
      startTime: Math.random() * Math.PI * 2,
    };
  }
  
  // Add the decorations group to the scene
  scene.add(decorations);
  
  // Return the decorations group for later animation
  return decorations;
};

// Add centerpiece for custom museum
const addCustomMuseumCenterpiece = (scene) => {
  console.log('Creating custom museum centerpiece');
  
  // Create the main centerpiece: a simple blue rotating torus knot like before
  const torusKnotGeometry = new THREE.TorusKnotGeometry(2, 0.6, 128, 16, 3, 5);
  const torusKnotMaterial = new THREE.MeshStandardMaterial({
    color: 0x1da1f2, // Twitter blue
    roughness: 0.2,
    metalness: 0.8,
    transparent: false,
  });
  
  const torusKnot = new THREE.Mesh(torusKnotGeometry, torusKnotMaterial);
  torusKnot.position.set(0, 6, 0); // Floating in the middle
  torusKnot.castShadow = true;
  torusKnot.receiveShadow = true;
  
  // Add wireframe outline for the torus knot
  const wireframeGeometry = new THREE.EdgesGeometry(torusKnotGeometry);
  const wireframeMaterial = new THREE.LineBasicMaterial({ 
    color: 0x000000, 
    linewidth: 2 
  });
  const wireframe = new THREE.LineSegments(wireframeGeometry, wireframeMaterial);
  torusKnot.add(wireframe);
  
  // Store animation data for the torus knot
  torusKnot.userData.animation = {
    rotationSpeed: {
      x: 0.01,
      y: 0.015,
      z: 0.005,
    },
    originalY: 6,
    floatSpeed: 0.8,
    floatHeight: 1.0,
    startTime: 0,
  };
  
  scene.add(torusKnot);
  console.log('Added rotating blue torus knot centerpiece');
  return torusKnot;
};

// Calculate content richness score for intelligent plane matching
const calculateContentRichness = (tweet) => {
  let score = 0;

  // Text content scoring. Guard against non-string text fields (e.g. null,
  // numbers, objects) — `text.length` on those is NaN/undefined and would
  // poison every `score +=` below, which then flows into a sort comparator.
  const rawText = tweet.text || tweet.full_text || tweet.content || '';
  const text = typeof rawText === 'string' ? rawText : '';
  score += text.length * 0.1; // Base text length
  
  // Bonus for longer, more substantial content
  if (text.length > 100) score += 20;
  if (text.length > 200) score += 30;
  if (text.length > 300) score += 50;
  
  // Image/media content scoring
  if (tweet.extended_entities?.media?.length > 0) {
    score += tweet.extended_entities.media.length * 25; // 25 points per image
  }
  
  // Attachments/links scoring
  if (tweet.entities?.urls?.length > 0) {
    score += tweet.entities.urls.length * 10; // 10 points per link
  }
  
  // Engagement scoring (if available)
  if (tweet.favorite_count) score += Math.log(tweet.favorite_count + 1) * 5;
  if (tweet.retweet_count) score += Math.log(tweet.retweet_count + 1) * 3;
  
  // Author information richness
  if (tweet.author || tweet.user?.screen_name) score += 5;
  if (tweet.user?.verified) score += 10;
  
  // Source diversity bonus
  if (isRoamSource(tweet.source)) score += 15; // Roam quotes tend to be more substantial
  if (tweet.source === 'twitter') score += 10;

  // Belt-and-suspenders: this score feeds an Array.prototype.sort comparator,
  // where a NaN result makes ordering nondeterministic. Never let a bad field
  // (of any kind, now or in the future) leak a non-finite value out of here.
  return Number.isFinite(score) ? Math.round(score) : 0;
};

// Measure a quad in its own plane rather than by a world-axis bounding box.
// The gallery's frames sit on angled walls, where an AABB reports the diagonal
// footprint and says nothing about the quad's proportions.
export const measurePlane = (mesh) => {
  const position = mesh.geometry?.attributes?.position;
  if (!position || position.count < 3) return null;

  const points = [];
  for (let i = 0; i < position.count; i++) {
    points.push(new THREE.Vector3().fromBufferAttribute(position, i).applyMatrix4(mesh.matrixWorld));
  }

  // Best-effort normal: the largest-area triangle among the first few corners.
  let normal = null;
  for (let i = 2; i < points.length; i++) {
    const n = new THREE.Vector3()
      .subVectors(points[i - 1], points[0])
      .cross(new THREE.Vector3().subVectors(points[i], points[0]));
    if (!normal || n.lengthSq() > normal.lengthSq()) normal = n;
  }
  if (!normal || normal.lengthSq() < 1e-20) return null;
  normal.normalize();

  // A quad's own edges, not a gravity-aligned bounding box. The gallery's
  // frames hang a few degrees off level and the UVs map corner-to-corner, so
  // the bounding box reports a wider, shorter rectangle than the card actually
  // gets stretched onto. Corner order varies (some quads are stored as a
  // winding cycle, some in grid order), so find the diagonal by distance.
  if (points.length === 4) {
    let far = 1;
    for (const i of [2, 3]) {
      if (points[i].distanceToSquared(points[0]) > points[far].distanceToSquared(points[0])) far = i;
    }
    const [a, b] = [1, 2, 3].filter(i => i !== far);
    // Average each edge with the one opposite it, so shear splits the difference.
    const edgeA = new THREE.Vector3().subVectors(points[a], points[0])
      .add(new THREE.Vector3().subVectors(points[far], points[b])).multiplyScalar(0.5);
    const edgeB = new THREE.Vector3().subVectors(points[b], points[0])
      .add(new THREE.Vector3().subVectors(points[far], points[a])).multiplyScalar(0.5);
    if (edgeA.lengthSq() > 1e-20 && edgeB.lengthSq() > 1e-20) {
      // Whichever edge stands closer to vertical is the quad's height.
      const upness = e => Math.abs(e.y) / e.length();
      const [w, h] = upness(edgeA) > upness(edgeB) ? [edgeB, edgeA] : [edgeA, edgeB];
      return { width: w.length(), height: h.length() };
    }
  }

  // Fallback for anything that is not a quad: gravity-aligned bounding box.
  const up = new THREE.Vector3(0, 1, 0).addScaledVector(normal, -normal.y);
  if (up.lengthSq() < 1e-12) up.copy(new THREE.Vector3(0, 0, 1)).addScaledVector(normal, -normal.z);
  up.normalize();
  const right = new THREE.Vector3().crossVectors(up, normal);

  let minU = Infinity, maxU = -Infinity, minV = Infinity, maxV = -Infinity;
  for (const p of points) {
    const u = p.dot(right);
    const v = p.dot(up);
    if (u < minU) minU = u;
    if (u > maxU) maxU = u;
    if (v < minV) minV = v;
    if (v > maxV) maxV = v;
  }
  return { width: maxU - minU, height: maxV - minV };
};

// Wrap a drawn card in a texture. glTF UVs put v=0 at the top of the image, so
// flipY must be off or every card renders upside down.
const createTweetCanvasTexture = (tweet, plane = null, text = null) => {
  const texture = new THREE.CanvasTexture(drawContentCanvas(tweet, plane, text));
  texture.needsUpdate = true;
  texture.flipY = false;
  return texture;
};

// Swap a mesh's material, releasing the old one *and its texture*. Disposing a
// material alone leaks the canvas texture, which is the expensive part here.
const replaceMaterial = (mesh, material) => {
  const previous = Array.isArray(mesh.material) ? mesh.material : [mesh.material];
  for (const mat of previous) {
    if (!mat) continue;
    mat.map?.dispose();
    mat.dispose();
  }
  mesh.material = material;
};

// Load custom museum from GLB file and populate with tweets
export const loadCustomMuseum = async (scene, tweets = [], modelConfig = MUSEUM_MODELS.gallery) => {
  const loader = new GLTFLoader();
  
  return new Promise((resolve, reject) => {
    console.log(`Loading ${modelConfig.name} from ${modelConfig.path} with scale ${modelConfig.scale}`);
    console.log('Model config:', modelConfig);
    
    loader.load(
      modelConfig.path,
      (gltf) => {
        console.log(`${modelConfig.name} loaded successfully`);
        const model = gltf.scene;
        
        // Find all objects with Tweet material and store them
        let tweetObjects = [];
        
        model.traverse((child) => {
          // Preserve Glass material with transparency
          if (child.material && child.material.name === 'Glass') {
            child.material.transparent = true;
            child.material.opacity = Math.min(child.material.opacity || 0.7, 0.8);
            console.log('Preserved Glass material with opacity:', child.material.opacity);
          }
          
          // Preserve Ground material - make it solid and non-reflective
          if (child.material && child.material.name === 'Ground') {
            // Force a completely new material to override any problematic settings
            const solidGroundMaterial = new THREE.MeshStandardMaterial({
              color: child.material.color || 0x888888,  // Keep original color or gray
              transparent: false,
              opacity: 1.0,
              roughness: 1.0,
              metalness: 0.0,
              wireframe: false,
              visible: true
            });
            child.material = solidGroundMaterial;
            console.log('Replaced Ground material with solid non-reflective version');
          }
          
          // Find objects with Tweet material
          if (child.material) {
            // Handle both single materials and material arrays
            const materials = Array.isArray(child.material) ? child.material : [child.material];
            const hasTweetMaterial = materials.some(mat => mat.name === 'Tweet');
            
            if (hasTweetMaterial) {
              tweetObjects.push(child);
              console.log('Found tweet object:', child.name || 'unnamed', 'with Tweet material');
            }
          }
        });
        
        // Measure every quad in its own plane. Sizes must be read before the
        // model is scaled and added, so the numbers stay in model units.
        model.updateMatrixWorld(true);
        const planesWithSize = tweetObjects
          .map(tweetObj => {
            const size = measurePlane(tweetObj);
            return size && { object: tweetObj, name: tweetObj.name || 'unnamed', size };
          })
          .filter(Boolean);

        // Match by how much text a plane can hold, not by area: a tall narrow
        // frame has plenty of area but fits very few characters per line.
        const textMetrics = textMetricsFor(planesWithSize.map(p => p.size));
        planesWithSize.forEach(p => { p.capacity = planeCapacity(p.size, textMetrics); });
        planesWithSize.sort((a, b) => b.capacity - a.capacity);

        // Update tweetObjects array with sorted planes
        tweetObjects = planesWithSize.map(plane => plane.object);

        // Longest content on the roomiest planes, so nothing gets ellipsised
        // that could have fitted elsewhere.
        const tweetsWithRichness = tweets.map(tweet => ({
          tweet: tweet,
          richness: calculateContentRichness(tweet),
          length: contentText(tweet).length
        }));

        tweetsWithRichness.sort((a, b) => b.length - a.length || b.richness - a.richness);

        // Extract sorted tweets
        const sortedTweets = tweetsWithRichness.map(item => item.tweet);

        console.log(`Museum: ${tweetObjects.length} planes, ${sortedTweets.length} tweets;`
          + ` plane capacity ${planesWithSize[planesWithSize.length - 1]?.capacity}`
          + `-${planesWithSize[0]?.capacity} chars`);
        if (tweetObjects.length > sortedTweets.length) {
          console.warn(`${tweetObjects.length - sortedTweets.length} planes will remain empty`);
        } else if (sortedTweets.length > tweetObjects.length) {
          console.warn(`${sortedTweets.length - tweetObjects.length} tweets will be unused`);
        }

        // Apply tweets to planes, roomiest first
        planesWithSize.forEach(({ object: tweetObj, size }, index) => {
          if (sortedTweets[index]) {
            const tweet = sortedTweets[index];

            try {
              const tweetTexture = createTweetCanvasTexture(tweet, size, textMetrics);

              if (!tweetTexture) throw new Error('Texture creation failed');

              replaceMaterial(tweetObj, new THREE.MeshBasicMaterial({
                map: tweetTexture,
                side: THREE.DoubleSide,  // Render both sides regardless of normals
                transparent: false,
                color: 0xffffff,
              }));
            } catch (error) {
              console.error(`Error texturing plane ${tweetObj.name || index}:`, error);
              replaceMaterial(tweetObj, new THREE.MeshStandardMaterial({
                color: 0xff4444, // Red flags a plane whose texture failed to build
                transparent: true,
                opacity: 0.5,
                side: THREE.DoubleSide,
              }));
            }
          } else {
            // No tweet for this plane: get out of the way and let the painting
            // underneath show through. The quad's own GLB material is no better
            // a fallback -- it is a placeholder texture with a red border and a
            // Top/Bottom alignment cross on it.
            tweetObj.visible = false;
          }
        });
        
        const successCount = tweetObjects.filter(o => o.material?.map).length;
        console.log(`Textured ${successCount}/${tweetObjects.length} planes`);
        
        // Apply scale to the entire model
        model.scale.set(modelConfig.scale, modelConfig.scale, modelConfig.scale);
        console.log(`Applied scale ${modelConfig.scale} to ${modelConfig.name}`);
        
        // Add model to scene
        scene.add(model);
        
        // Debug: Log model bounding box to help with camera positioning
        const box = new THREE.Box3().setFromObject(model);
        const size = box.getSize(new THREE.Vector3());
        const center = box.getCenter(new THREE.Vector3());
        console.log(`${modelConfig.name} bounding box:`, {
          size: { x: size.x.toFixed(2), y: size.y.toFixed(2), z: size.z.toFixed(2) },
          center: { x: center.x.toFixed(2), y: center.y.toFixed(2), z: center.z.toFixed(2) }
        });
        
        // Add floating decorative elements (but not the static museum parts)
        addDecorativeElements(scene);
        
        // Add centerpiece torus knot and store reference
        const centerpiece = addCustomMuseumCenterpiece(scene);
        
        // Store centerpiece reference for animation (need to pass this back)
        scene.userData.centerpiece = centerpiece;
        
        // Add additional lighting for better tweet visibility
        const ambientLight = new THREE.AmbientLight(0x404040, 0.6);
        ambientLight.name = 'CustomMuseum_AmbientLight';
        scene.add(ambientLight);
        
        const directionalLight = new THREE.DirectionalLight(0xffffff, 0.8);
        directionalLight.position.set(10, 10, 5);
        directionalLight.name = 'CustomMuseum_DirectionalLight';
        scene.add(directionalLight);
        
        console.log(`Custom museum loaded with ${tweetObjects.length} tweet objects`);
        resolve({ model, tweetObjects });
      },
      (progress) => {
        const percent = (progress.loaded / progress.total) * 100;
        console.log(`Loading ${modelConfig.name}: ${percent.toFixed(1)}%`);
      },
      (error) => {
        console.error(`Error loading ${modelConfig.name}:`, error);
        console.log(`GLB loading failed for ${modelConfig.path} - file may not exist or be corrupted`);
        reject(error);
      }
    );
  });
};

// Create an architectural museum layout using the layout manager

export const createMuseumArchitecture = (scene, tweets, clusterData = null) => {
  // Create a group for the entire museum
  const museum = new THREE.Group();
  
  // Create a layout manager
  const layoutManager = new MuseumLayoutManager();
  
  // Generate radial layout with central and satellite domes
  // Let the layout manager use its default values which are now larger
  layoutManager.generateRadialLayout();
  
  // Create domes for each room in the layout
  layoutManager.rooms.forEach(roomNode => {
    const dome = createDome(roomNode);
    museum.add(dome);
    
    // Store reference to the dome in the room node for later use
    roomNode.dome = dome;
    
    // Add classical architectural elements based on room type
    createClassicalElements(museum, roomNode);
  });
  
  // Create hallways between connected rooms - with debug logging
  console.log(`Creating ${layoutManager.hallways.size} hallways`);
  
  // Convert to array and ensure we process hallways in a consistent order
  const hallwaysArray = Array.from(layoutManager.hallways.values());
  
  // Sort hallways by id for consistent processing order
  hallwaysArray.sort((a, b) => a.id.localeCompare(b.id));
  
  // Create each hallway
  hallwaysArray.forEach((hallway, index) => {
    console.log(`Creating hallway ${index + 1}/${hallwaysArray.length}: ${hallway.id}`);
    console.log(`  From: ${hallway.startRoomId} to ${hallway.endRoomId}`);
    createHallway(hallway, museum);
  });
  
  // Distribute tweets among the domes if available
  if (tweets && tweets.length > 0) {
    // Group tweets by the number of rooms
    const roomCount = layoutManager.rooms.size;
    const tweetsPerRoom = Math.ceil(tweets.length / roomCount);
    
    let tweetIndex = 0;
    layoutManager.rooms.forEach((roomNode) => {
      const roomTweets = tweets.slice(tweetIndex, tweetIndex + tweetsPerRoom);
      tweetIndex += tweetsPerRoom;
      
      if (roomTweets.length > 0) {
        // Pass the room node to provide access to entrances for frame placement
        addTweetFramesToDome(roomNode.dome, roomTweets, roomNode);
      } else {
        // Add placeholder frames if no tweets available for this room
        addPlaceholderFramesToDome(roomNode.dome, 8, roomNode);
      }
    });
  } else {
    // Add placeholder frames to all domes if no tweets
    layoutManager.rooms.forEach((roomNode) => {
      addPlaceholderFramesToDome(roomNode.dome, 8, roomNode);
    });
  }
  
  // Add museum to the scene
  scene.add(museum);
  
  // Add floor - much larger to accommodate larger buildings
  const floorGeometry = new THREE.CircleGeometry(100, 64);
  const floorMaterial = new THREE.MeshStandardMaterial({
    color: COLORS.walls,
    roughness: 0.8,
    metalness: 0.2,
  });
  
  const floor = new THREE.Mesh(floorGeometry, floorMaterial);
  floor.rotation.x = -Math.PI / 2;
  floor.position.y = -0.1;
  floor.receiveShadow = true;
  scene.add(floor);
  
  // Add wireframe to the floor
  const floorWireframe = new THREE.LineSegments(
    new THREE.EdgesGeometry(floorGeometry),
    new THREE.LineBasicMaterial({ color: COLORS.wireframe, linewidth: 1 })
  );
  floorWireframe.rotation.x = -Math.PI / 2;
  floorWireframe.position.y = -0.05;
  scene.add(floorWireframe);
  
  // Add floating decorative elements
  addDecorativeElements(scene);
  
  // Return the museum group and layout manager for potential later use
  return { museum, layoutManager };
};

// Export scene to Three.js Editor compatible JSON
export const exportSceneToJSON = (scene) => {
  // Create a clean scene for export
  const exportScene = new THREE.Scene();
  
  // Function to recursively clone objects while preserving hierarchy
  const cloneObjectRecursive = (source, parent = exportScene) => {
    if (source.isMesh && source.geometry) {
      // Create simplified material
      let material;
      
      // Determine material type and color
      if (source.material.map) {
        // For textured materials (like tweet frames), create colored material
        material = new THREE.MeshStandardMaterial({
          color: 0xffffff,
          transparent: true,
          opacity: 0.8
        });
      } else {
        // Use existing color or default
        material = new THREE.MeshStandardMaterial({
          color: source.material.color || COLORS.walls,
          wireframe: source.material.wireframe || false,
          transparent: source.material.transparent || false,
          opacity: source.material.opacity || 1.0
        });
      }
      
      // Clone geometry
      const geometry = source.geometry.clone();
      const mesh = new THREE.Mesh(geometry, material);
      
      // Copy transform properties
      mesh.position.copy(source.position);
      mesh.rotation.copy(source.rotation);
      mesh.scale.copy(source.scale);
      
      // Generate meaningful names
      if (source.parent && source.parent.userData && source.parent.userData.hallway) {
        mesh.name = `Hallway_${source.parent.userData.hallway.id}_${geometry.type}`;
      } else if (geometry.type === 'SphereGeometry') {
        mesh.name = `Dome_${geometry.type}`;
      } else if (geometry.type === 'CylinderGeometry') {
        mesh.name = `Wall_${geometry.type}`;
      } else if (geometry.type === 'PlaneGeometry') {
        mesh.name = `TweetFrame_${Math.random().toString(36).substr(2, 6)}`;
      } else if (geometry.type === 'BoxGeometry') {
        mesh.name = `Structure_${geometry.type}`;
      } else {
        mesh.name = `${geometry.type}_${Math.random().toString(36).substr(2, 6)}`;
      }
      
      parent.add(mesh);
      return mesh;
    } else if (source.isGroup) {
      // Create group and recursively add children
      const group = new THREE.Group();
      group.position.copy(source.position);
      group.rotation.copy(source.rotation);
      group.scale.copy(source.scale);
      
      // Generate group name
      if (source.userData && source.userData.hallway) {
        group.name = `Hallway_${source.userData.hallway.id}`;
      } else {
        group.name = `Group_${Math.random().toString(36).substr(2, 6)}`;
      }
      
      parent.add(group);
      
      // Recursively process children
      source.children.forEach(child => {
        cloneObjectRecursive(child, group);
      });
      
      return group;
    }
    return null;
  };
  
  // Process scene children, but skip certain objects
  scene.children.forEach(child => {
    // Skip sky, lights, and other non-architectural elements
    if (child.isLight || 
        child.material?.uniforms || // Sky material
        child.name?.includes('Sky') ||
        child.name?.includes('Helper')) {
      return;
    }
    
    cloneObjectRecursive(child);
  });
  
  // Add placeholder planes for tweet positions (empty frames users can position)
  const addTweetPlaceholders = () => {
    const placeholderGroup = new THREE.Group();
    placeholderGroup.name = 'TweetPlaceholders';
    
    // Create 12 placeholder frames
    for (let i = 0; i < 12; i++) {
      const frameGeometry = new THREE.PlaneGeometry(4.5, 3);
      const frameMaterial = new THREE.MeshStandardMaterial({
        color: 0x1da1f2,
        transparent: true,
        opacity: 0.3,
        side: THREE.DoubleSide
      });
      
      const frame = new THREE.Mesh(frameGeometry, frameMaterial);
      frame.name = `TweetPlaceholder_${i + 1}`;
      
      // Position them in a circle around origin for easy placement
      const angle = (i / 12) * Math.PI * 2;
      const radius = 15;
      frame.position.set(
        radius * Math.cos(angle),
        4,
        radius * Math.sin(angle)
      );
      frame.lookAt(0, 4, 0); // Face inward
      
      placeholderGroup.add(frame);
    }
    
    exportScene.add(placeholderGroup);
  };
  
  // Add placeholder frames
  addTweetPlaceholders();
  
  // Add basic lighting for the editor
  const ambientLight = new THREE.AmbientLight(0x404040, 0.6);
  ambientLight.name = 'AmbientLight';
  exportScene.add(ambientLight);
  
  const directionalLight = new THREE.DirectionalLight(0xffffff, 0.8);
  directionalLight.position.set(50, 50, 50);
  directionalLight.name = 'DirectionalLight';
  exportScene.add(directionalLight);
  
  // Export to JSON
  const json = exportScene.toJSON();
  
  // Create downloadable blob
  const dataStr = JSON.stringify(json, null, 2);
  const dataBlob = new Blob([dataStr], { type: 'application/json' });
  
  // Create download link
  const url = URL.createObjectURL(dataBlob);
  const link = document.createElement('a');
  link.href = url;
  link.download = 'museum_architecture.json';
  link.click();
  
  // Cleanup
  URL.revokeObjectURL(url);
  
  console.log('Exported scene with', exportScene.children.length, 'top-level objects');
  return json;
};

// Export scene to GLB format for Blender/other 3D software
export const exportSceneToGLB = async (scene) => {
  try {
    // Import GLTFExporter dynamically
    const { GLTFExporter } = await import('three/examples/jsm/exporters/GLTFExporter.js');
  
  // Create a clean scene for export (reuse the logic from JSON export)
  const exportScene = new THREE.Scene();
  
  // Reuse the cloning logic from exportSceneToJSON
  const cloneObjectRecursive = (source, parent = exportScene) => {
    if (source.isMesh && source.geometry) {
      let material;
      
      if (source.material.map) {
        material = new THREE.MeshStandardMaterial({
          color: 0xffffff,
          transparent: true,
          opacity: 0.8
        });
      } else {
        material = new THREE.MeshStandardMaterial({
          color: source.material.color || COLORS.walls,
          wireframe: false,
          transparent: source.material.transparent || false,
          opacity: source.material.opacity || 1.0
        });
      }
      
      const geometry = source.geometry.clone();
      const mesh = new THREE.Mesh(geometry, material);
      
      mesh.position.copy(source.position);
      mesh.rotation.copy(source.rotation);
      mesh.scale.copy(source.scale);
      
      if (source.parent && source.parent.userData && source.parent.userData.hallway) {
        mesh.name = `Hallway_${source.parent.userData.hallway.id}_${geometry.type}`;
      } else if (geometry.type === 'SphereGeometry') {
        mesh.name = `Dome_${geometry.type}`;
      } else if (geometry.type === 'CylinderGeometry') {
        mesh.name = `Wall_${geometry.type}`;
      } else if (geometry.type === 'PlaneGeometry') {
        mesh.name = `TweetFrame_${Math.random().toString(36).substr(2, 6)}`;
      } else if (geometry.type === 'BoxGeometry') {
        mesh.name = `Structure_${geometry.type}`;
      } else {
        mesh.name = `${geometry.type}_${Math.random().toString(36).substr(2, 6)}`;
      }
      
      parent.add(mesh);
      return mesh;
    } else if (source.isGroup) {
      const group = new THREE.Group();
      group.position.copy(source.position);
      group.rotation.copy(source.rotation);
      group.scale.copy(source.scale);
      
      if (source.userData && source.userData.hallway) {
        group.name = `Hallway_${source.userData.hallway.id}`;
      } else {
        group.name = `Group_${Math.random().toString(36).substr(2, 6)}`;
      }
      
      parent.add(group);
      
      source.children.forEach(child => {
        cloneObjectRecursive(child, group);
      });
      
      return group;
    }
    return null;
  };
  
  // Process scene children
  scene.children.forEach(child => {
    if (child.isLight || 
        child.material?.uniforms ||
        child.name?.includes('Sky') ||
        child.name?.includes('Helper')) {
      return;
    }
    
    cloneObjectRecursive(child);
  });
  
  // Add tweet placeholders
  const placeholderGroup = new THREE.Group();
  placeholderGroup.name = 'TweetPlaceholders';
  
  for (let i = 0; i < 12; i++) {
    const frameGeometry = new THREE.PlaneGeometry(4.5, 3);
    const frameMaterial = new THREE.MeshStandardMaterial({
      color: 0x1da1f2,
      transparent: true,
      opacity: 0.3,
      side: THREE.DoubleSide
    });
    
    const frame = new THREE.Mesh(frameGeometry, frameMaterial);
    frame.name = `TweetPlaceholder_${i + 1}`;
    
    const angle = (i / 12) * Math.PI * 2;
    const radius = 15;
    frame.position.set(
      radius * Math.cos(angle),
      4,
      radius * Math.sin(angle)
    );
    frame.lookAt(0, 4, 0);
    
    placeholderGroup.add(frame);
  }
  
  exportScene.add(placeholderGroup);
  
    // Export to GLB
    const exporter = new GLTFExporter();
    
    return new Promise((resolve, reject) => {
      exporter.parse(
        exportScene,
        (result) => {
          // Create downloadable blob
          const blob = new Blob([result], { type: 'application/octet-stream' });
          const url = URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = url;
          link.download = 'museum_architecture.glb';
          link.click();
          
          // Cleanup
          URL.revokeObjectURL(url);
          
          console.log('Exported GLB with', exportScene.children.length, 'top-level objects');
          resolve(result);
        },
        (error) => {
          console.error('GLB export failed:', error);
          reject(error);
        },
        { binary: true }
      );
    });
  } catch (error) {
    console.error('GLTFExporter import failed:', error);
    alert('GLB export failed. Please try the JSON export instead.');
    throw error;
  }
};